/**
 * Registered once in the router registry: the legacy tech-ui section and the
 * migrated connect feature share this one in-memory fixture, so the two Playwright
 * projects compare row for row.
 */

import type { ChargeDetailRecord } from "./charge-detail-records/generators";
import {
  FIRST_CANDIDATE_SESSION,
  SECOND_CANDIDATE_SESSION,
  SEEDED_LINKED_CDRS,
  SEEDED_NON_LINKED_CDRS,
  SESSION_ON_LOWERCASE_PAYEE_ITEM,
  SESSION_SPANNING_TWO_DAYS,
  SESSION_WHOSE_CHART_FAILS,
  SESSION_WITH_MEASUREMENTS,
  SESSION_WITH_NO_MEASUREMENTS,
} from "./charge-detail-records/stubs";
import {
  SEEDED_CHARGE_RECORDS,
  SEEDED_CHART_SUMMARY,
  SEEDED_METER_DATA,
  SEEDED_PRIORITY_TRANSACTIONS,
} from "./charge-records/stubs";
import type { MeterDatum } from "./charge-records/stubs";
import type { ExpenseItem } from "./expense-items/generators";
import {
  EXPENSE_DOWNLOAD_FAILS,
  EXPENSE_LOWERCASE_PAYEE,
  EXPENSE_ON_ACTIVE_REPORT,
  EXPENSE_ON_APPROVED_REPORT,
  EXPENSE_PAID_WITH_SESSIONS,
} from "./expense-items/stubs";
import type { ExpenseReportSummary } from "./expense-reports/generators";
import {
  ACTIVE_CLEAN,
  ACTIVE_INVALID,
  APPROVED,
  AWAITING_EARLIER_REPORTS,
  CURRENCY_ABSENT,
  CURRENCY_NULL,
  DOWNLOAD_FAILS,
  FUNDS_RECEIVED,
  INVALID_BENEFICIARY,
  KYC_NOT_PASSED,
  MISSING_RATES,
  PENDING_CLEAN,
  PENDING_INVALID,
  SEEDED_EXPENSES_BY_REPORT,
  SEEDED_REPORTS,
  UNREADABLE_REPORT_ROW,
} from "./expense-reports/stubs";
import type { PaginatedResponse } from "./shared";

const REIMBURSEMENTS_DATASET_STORAGE_KEY = "e2e:reimbursements-dataset";

type ReimbursementsDataset =
  | "full"
  | "empty-reports"
  | "empty-expenses"
  | "empty-cdrs"
  | "empty-candidates";

function readStoredDataset(): string | null {
  try {
    return (
      globalThis.localStorage?.getItem(REIMBURSEMENTS_DATASET_STORAGE_KEY) ??
      null
    );
  } catch {
    return null;
  }
}

function readDataset(): ReimbursementsDataset {
  const raw = readStoredDataset();
  if (
    raw === "empty-reports" ||
    raw === "empty-expenses" ||
    raw === "empty-cdrs" ||
    raw === "empty-candidates"
  ) {
    return raw;
  }
  return "full";
}

interface ReimbursementsState {
  reports: ExpenseReportSummary[];
  expensesByReport: Record<number, ExpenseItem[]>;
  linkedCdrs: Record<string, ChargeDetailRecord[]>;
  nonLinkedCdrs: ChargeDetailRecord[];
}

function seedState(): ReimbursementsState {
  return {
    reports: SEEDED_REPORTS.map((report) => ({ ...report })),
    expensesByReport: Object.fromEntries(
      Object.entries(SEEDED_EXPENSES_BY_REPORT).map(([reportId, items]) => [
        reportId,
        items.map((item) => ({ ...item })),
      ])
    ),
    linkedCdrs: Object.fromEntries(
      Object.entries(SEEDED_LINKED_CDRS).map(([expenseId, records]) => [
        expenseId,
        records.map((record) => ({ ...record })),
      ])
    ),
    nonLinkedCdrs: SEEDED_NON_LINKED_CDRS.map((record) => ({ ...record })),
  };
}

let state = seedState();

function resetReimbursements(): void {
  state = seedState();
}

function paginate<T>(
  rows: T[],
  page: number,
  limit: number
): PaginatedResponse<T> {
  const safeLimit = limit > 0 ? limit : 24;
  const safePage = page > 0 ? page : 1;
  const start = (safePage - 1) * safeLimit;
  const window = rows.slice(start, start + safeLimit);

  return {
    data: window,
    pagination: {
      currentPage: safePage,
      itemCount: window.length,
      itemsPerPage: safeLimit,
      totalItems: rows.length,
      totalPages: Math.ceil(rows.length / safeLimit),
    },
  };
}

function listReports(page: number, limit: number): PaginatedResponse<unknown> {
  if (readDataset() === "empty-reports") {
    return paginate([], page, limit);
  }

  const result = paginate(state.reports, page, limit);
  if (result.pagination.currentPage === 1) {
    return {
      ...result,
      data: [...result.data, UNREADABLE_REPORT_ROW],
      pagination: {
        ...result.pagination,
        itemCount: result.data.length + 1,
      },
    };
  }
  return result;
}

function reportById(reportId: number): ExpenseReportSummary | undefined {
  return state.reports.find((report) => report.id === reportId);
}

function getReport(
  reportId: number
): { report: ExpenseReportSummary; expenses: ExpenseItem[] } | undefined {
  const report = reportById(reportId);
  if (!report) return undefined;

  const expenses =
    readDataset() === "empty-expenses"
      ? []
      : (state.expensesByReport[reportId] ?? []);

  return { report, expenses };
}

function expenseById(expenseId: string): ExpenseItem | undefined {
  return Object.values(state.expensesByReport)
    .flat()
    .find((item) => item.id === expenseId);
}

function getExpenseDetails(
  expenseId: string
): (ExpenseItem & { cdrList: ChargeDetailRecord[] }) | undefined {
  const expense = expenseById(expenseId);
  if (!expense) return undefined;

  const cdrList =
    readDataset() === "empty-cdrs" ? [] : (state.linkedCdrs[expenseId] ?? []);

  return { ...expense, cdrList };
}

function listNonLinkedCdrs(page: number, limit: number) {
  const rows = readDataset() === "empty-candidates" ? [] : state.nonLinkedCdrs;
  return paginate(rows, page, limit);
}

function getChargeRecord(
  transactionId: number
): Record<string, unknown> | undefined {
  return SEEDED_CHARGE_RECORDS[transactionId];
}

function getChartMeterData(transactionId: number): MeterDatum[] {
  return transactionId === SESSION_WITH_MEASUREMENTS.transactionId
    ? SEEDED_METER_DATA
    : [];
}

function getChartSummary() {
  return SEEDED_CHART_SUMMARY;
}

function listPriorityTransactions() {
  return { data: SEEDED_PRIORITY_TRANSACTIONS };
}

function approveReport(reportId: number): boolean {
  const report = reportById(reportId);
  if (!report) return false;
  report.status = "Approved";
  return true;
}

function addCdrsToExpense(
  expenseId: string,
  transactionIds: number[]
): boolean {
  if (!expenseById(expenseId)) return false;

  const moved = state.nonLinkedCdrs.filter((record) =>
    transactionIds.includes(record.transactionId)
  );
  state.nonLinkedCdrs = state.nonLinkedCdrs.filter(
    (record) => !transactionIds.includes(record.transactionId)
  );
  state.linkedCdrs[expenseId] = [
    ...(state.linkedCdrs[expenseId] ?? []),
    ...moved,
  ];
  return true;
}

function removeCdrsFromExpense(
  expenseId: string,
  transactionIds: number[]
): boolean {
  const linked = state.linkedCdrs[expenseId];
  if (!linked) return false;

  const removed = linked.filter((record) =>
    transactionIds.includes(record.transactionId)
  );
  state.linkedCdrs[expenseId] = linked.filter(
    (record) => !transactionIds.includes(record.transactionId)
  );
  state.nonLinkedCdrs = [...state.nonLinkedCdrs, ...removed];
  return true;
}

export {
  REIMBURSEMENTS_DATASET_STORAGE_KEY,
  ACTIVE_CLEAN,
  ACTIVE_INVALID,
  APPROVED,
  AWAITING_EARLIER_REPORTS,
  CURRENCY_ABSENT,
  CURRENCY_NULL,
  DOWNLOAD_FAILS,
  EXPENSE_DOWNLOAD_FAILS,
  EXPENSE_LOWERCASE_PAYEE,
  EXPENSE_ON_ACTIVE_REPORT,
  EXPENSE_ON_APPROVED_REPORT,
  EXPENSE_PAID_WITH_SESSIONS,
  FUNDS_RECEIVED,
  INVALID_BENEFICIARY,
  KYC_NOT_PASSED,
  MISSING_RATES,
  PENDING_CLEAN,
  PENDING_INVALID,
  FIRST_CANDIDATE_SESSION,
  SECOND_CANDIDATE_SESSION,
  SESSION_ON_LOWERCASE_PAYEE_ITEM,
  SESSION_SPANNING_TWO_DAYS,
  SESSION_WHOSE_CHART_FAILS,
  SESSION_WITH_MEASUREMENTS,
  SESSION_WITH_NO_MEASUREMENTS,
  addCdrsToExpense,
  approveReport,
  getChargeRecord,
  getChartMeterData,
  getChartSummary,
  getExpenseDetails,
  getReport,
  listNonLinkedCdrs,
  listPriorityTransactions,
  listReports,
  removeCdrsFromExpense,
  resetReimbursements,
};
export { generateMockExpenseItem } from "./expense-items/generators";
export { generateMockExpenseReport } from "./expense-reports/generators";
export { generateMockChargeDetailRecord } from "./charge-detail-records/generators";
export type { ChargeDetailRecord } from "./charge-detail-records/generators";
export type { MeterDatum } from "./charge-records/stubs";
export type { ExpenseItem, PaymentStatus } from "./expense-items/generators";
export type {
  ExpenseReportSummary,
  InvalidInformation,
} from "./expense-reports/generators";
export type { ExpenseReportStatus } from "./shared";
export type { ReimbursementsDataset };
